/**
 * Mitigation Detail Page
 *
 * Full page view of a mitigation's details.
 * Similar to RiskDetail page structure.
 */

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Button,
  LinearProgress,
  CircularProgress,
  Alert,
  IconButton,
  Breadcrumbs,
  Link,
  Tooltip,
  Card,
  CardContent,
  TextField,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Shield as ShieldIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Security as SecurityIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  History as HistoryIcon,
  Delete as DeleteIcon,
  OpenInNew as OpenInNewIcon,
  BugReport as BugIcon,
  Save as SaveIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { EntityType, UpdateMitigationDto } from 'shared';
import { mitigationApi, riskApi } from '../services/risk.service';
import {
  useUpdateMitigation,
  mitigationQueryKeys,
  riskQueryKeys,
} from '../hooks/useRisks';
import { MitigationForm } from '../components/MitigationForm';
import { TransitionHistory, VersionHistoryDialog } from '../components/History';
import { ISO_CONTROLS } from '../../../shared/src/catalogs/iso27001';
import { getD3fendUrl } from '../../../shared/src/catalogs/mitre-d3fend';

// =============================================================================
// HELPER COMPONENTS
// =============================================================================

interface StatusChipProps {
  status: string;
  size?: 'small' | 'medium';
}

const StatusChip: React.FC<StatusChipProps> = ({ status, size = 'medium' }) => {
  const { t } = useTranslation();

  const getStatusConfig = (s: string) => {
    switch (s) {
      case 'verified':
        return {
          color: '#42B8B2',
          bgColor: '#42B8B220',
          label: t('mitigation.status.verified'),
        };
      case 'implemented':
        return {
          color: '#42B8B2',
          bgColor: '#42B8B220',
          label: t('mitigation.status.implemented'),
        };
      case 'in_progress':
        return {
          color: '#FFBB00',
          bgColor: '#FFBB0020',
          label: t('mitigation.status.in_progress'),
        };
      case 'planned':
      default:
        return {
          color: '#939597',
          bgColor: '#93959720',
          label: t('mitigation.status.planned'),
        };
    }
  };

  const config = getStatusConfig(status);

  return (
    <Chip
      label={config.label}
      size={size}
      sx={{
        backgroundColor: config.bgColor,
        color: config.color,
        fontWeight: 600,
      }}
    />
  );
};

interface PriorityChipProps {
  priority: string;
  size?: 'small' | 'medium';
}

const PriorityChip: React.FC<PriorityChipProps> = ({
  priority,
  size = 'medium',
}) => {
  const { t } = useTranslation();

  const getColor = (): string => {
    switch (priority) {
      case 'critical':
        return '#FF5252';
      case 'high':
        return '#2D3B45';
      case 'medium':
        return '#FFBB00';
      case 'low':
      default:
        return '#42B8B2';
    }
  };

  const color = getColor();

  return (
    <Chip
      label={t(`common.priority.${priority}`)}
      size={size}
      sx={{
        backgroundColor: `${color}20`,
        color: color,
        fontWeight: 600,
      }}
    />
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const MitigationDetail: React.FC = () => {
  const { mitigationId } = useParams<{ mitigationId: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  // Mutations
  const updateMitigationMutation = useUpdateMitigation();

  // Local state
  const [formOpen, setFormOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [editingOwner, setEditingOwner] = useState(false);
  const [ownerValue, setOwnerValue] = useState('');
  const [savingOwner, setSavingOwner] = useState(false);

  // Fetch mitigation data
  const {
    data: mitigation,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: mitigationQueryKeys.detail(mitigationId || ''),
    queryFn: () => {
      if (!mitigationId) throw new Error('Missing mitigationId');
      return mitigationApi.getById(mitigationId);
    },
    enabled: !!mitigationId,
  });

  // Fetch parent risk data
  const { data: risk } = useQuery({
    queryKey: riskQueryKeys.detail(mitigation?.riskId || ''),
    queryFn: () => {
      if (!mitigation?.riskId) throw new Error('Missing riskId');
      return riskApi.getById(mitigation.riskId);
    },
    enabled: !!mitigation?.riskId,
  });

  // Handle form submit
  const handleFormSubmit = async (data: UpdateMitigationDto) => {
    if (!mitigationId) return;

    try {
      await updateMitigationMutation.mutateAsync({ id: mitigationId, data });
      setFormOpen(false);
      // Queries will be invalidated automatically by the mutation hook
    } catch (error) {
      console.error('Failed to update mitigation:', error);
      // The mutation hook will handle error display
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!mitigation) return;

    // Using globalThis for confirm
    const confirmed = globalThis.confirm(t('mitigation.delete.confirm'));
    if (!confirmed) return;

    try {
      await mitigationApi.delete(mitigation.id);
      // Navigate back to risk detail
      navigate(`/risks/${mitigation.riskId}`);
    } catch (err) {
      console.error('Failed to delete mitigation:', err);
    }
  };

  // Handle owner inline edit
  const handleStartEditOwner = () => {
    setOwnerValue(mitigation?.owner || '');
    setEditingOwner(true);
  };

  const handleCancelEditOwner = () => {
    setEditingOwner(false);
    setOwnerValue('');
  };

  const handleSaveOwner = async () => {
    if (!mitigationId) return;
    setSavingOwner(true);
    try {
      await mitigationApi.updateMitigation(mitigationId, { owner: ownerValue });
      queryClient.invalidateQueries({
        queryKey: mitigationQueryKeys.detail(mitigationId),
      });
      setEditingOwner(false);
    } catch (error) {
      console.error('Failed to update owner:', error);
    } finally {
      setSavingOwner(false);
    }
  };

  // Calculate effectiveness percentage
  const effectivenessPercent = Math.round(
    (mitigation?.reductionFactor || 0) * 100
  );

  // Loading state
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (isError || !mitigation) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {error instanceof Error ? error.message : t('mitigation.loadError')}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mt: 2 }}
        >
          {t('common.back')}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body2"
          onClick={() => navigate('/risks')}
          sx={{ textDecoration: 'none', cursor: 'pointer' }}
        >
          {t('nav.risks')}
        </Link>
        {risk && (
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate(`/risks/${risk.id}`)}
            sx={{ textDecoration: 'none', cursor: 'pointer' }}
          >
            {risk.title}
          </Link>
        )}
        <Typography color="text.primary">{mitigation.title}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
            <IconButton onClick={() => navigate(-1)} sx={{ mr: 1 }}>
              <ArrowBackIcon />
            </IconButton>
            <ShieldIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" component="h1" fontWeight={600}>
                {mitigation.title}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <StatusChip status={mitigation.implementationStatus} />
                <PriorityChip priority={mitigation.priority} />
                <Chip
                  label={t(`mitigation.controlType.${mitigation.controlType}`)}
                  variant="outlined"
                  size="medium"
                />
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title={t('history.viewHistory')}>
              <IconButton onClick={() => setHistoryDialogOpen(true)}>
                <HistoryIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => setFormOpen(true)}
            >
              {t('common.edit')}
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
            >
              {t('common.delete')}
            </Button>
          </Box>
        </Box>

        {/* Description */}
        {mitigation.description && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {mitigation.description}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Metadata Section - Similar to RiskDetail */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {t('mitigation.ownershipDates')}
        </Typography>
        <Grid container spacing={2} alignItems="center">
          {/* Owner with inline edit */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('mitigation.fields.owner')}
            </Typography>
            {editingOwner ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TextField
                  size="small"
                  value={ownerValue}
                  onChange={e => setOwnerValue(e.target.value)}
                  placeholder={t('mitigation.fields.owner')}
                  autoFocus
                  sx={{ minWidth: 120 }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') handleSaveOwner();
                    if (e.key === 'Escape') handleCancelEditOwner();
                  }}
                />
                <IconButton
                  size="small"
                  color="primary"
                  onClick={handleSaveOwner}
                  disabled={savingOwner}
                >
                  {savingOwner ? (
                    <CircularProgress size={16} />
                  ) : (
                    <SaveIcon fontSize="small" />
                  )}
                </IconButton>
                <IconButton
                  size="small"
                  onClick={handleCancelEditOwner}
                  disabled={savingOwner}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PersonIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="body1">
                  {mitigation.owner || t('common.notSpecified')}
                </Typography>
                <Tooltip title={t('common.edit')}>
                  <IconButton size="small" onClick={handleStartEditOwner}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Grid>

          {/* Due Date */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('mitigation.fields.dueDate')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CalendarIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Typography variant="body1">
                {mitigation.dueDate
                  ? new Date(mitigation.dueDate).toLocaleDateString()
                  : t('common.notSpecified')}
              </Typography>
            </Box>
          </Grid>

          {/* Implemented At */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('mitigation.fields.implementedAt')}
            </Typography>
            <Typography variant="body1">
              {mitigation.implementedAt
                ? new Date(mitigation.implementedAt).toLocaleDateString()
                : t('common.notSpecified')}
            </Typography>
          </Grid>

          {/* Verified At */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('mitigation.fields.verifiedAt')}
            </Typography>
            <Typography variant="body1">
              {mitigation.verifiedAt
                ? new Date(mitigation.verifiedAt).toLocaleDateString()
                : t('common.notSpecified')}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={3}>
        {/* Left Column - Main Details */}
        <Grid size={{ xs: 12, md: 8 }}>
          {/* Effectiveness Card */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('mitigation.fields.effectiveness')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Box sx={{ flex: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={effectivenessPercent}
                  sx={{
                    height: 16,
                    borderRadius: 8,
                    backgroundColor: '#e0e0e0',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: '#42B8B2',
                      borderRadius: 8,
                    },
                  }}
                />
              </Box>
              <Typography
                variant="h4"
                sx={{ color: '#42B8B2', fontWeight: 700, minWidth: 80 }}
              >
                {effectivenessPercent}%
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('mitigation.effectivenessHelp')}
            </Typography>
          </Paper>

          {/* CIA Applies To */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('mitigation.fields.appliesTo')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {(mitigation.appliesTo || []).map(dimension => {
                const colors: Record<string, string> = {
                  confidentiality: '#2D3B45',
                  integrity: '#00D9FF',
                  availability: '#FFB300',
                };
                const color = colors[dimension] || '#939597';
                return (
                  <Chip
                    key={dimension}
                    label={t(`common.cia.${dimension}`)}
                    sx={{
                      borderColor: color,
                      color: color,
                      backgroundColor: `${color}10`,
                      fontWeight: 600,
                    }}
                    variant="outlined"
                  />
                );
              })}
              {(!mitigation.appliesTo || mitigation.appliesTo.length === 0) && (
                <Typography variant="body2" color="text.secondary">
                  {t('common.notSpecified')}
                </Typography>
              )}
            </Box>
          </Paper>

          {/* Framework References */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('mitigation.frameworkReferences')}
            </Typography>
            <Grid container spacing={3}>
              {/* ISO Control */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <SecurityIcon sx={{ color: 'primary.main' }} />
                      <Typography variant="subtitle2" color="text.secondary">
                        {t('mitigation.fields.isoControl')}
                      </Typography>
                    </Box>
                    {mitigation.isoControlId ? (
                      (() => {
                        const isoControl = ISO_CONTROLS.find(
                          c => c.id === mitigation.isoControlId
                        );
                        const isNorwegian =
                          i18n.language === 'nb' || i18n.language === 'no';
                        const controlName = isoControl
                          ? isNorwegian
                            ? isoControl.nameNb
                            : isoControl.name
                          : mitigation.isoControlName;
                        const controlDescription = isoControl
                          ? isNorwegian
                            ? isoControl.descriptionNb
                            : isoControl.description
                          : null;
                        return (
                          <>
                            <Typography variant="h6" fontWeight={600}>
                              {mitigation.isoControlId}
                            </Typography>
                            {controlName && (
                              <Typography
                                variant="body2"
                                fontWeight={500}
                                sx={{ mt: 0.5 }}
                              >
                                {controlName}
                              </Typography>
                            )}
                            {controlDescription && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 1 }}
                              >
                                {controlDescription}
                              </Typography>
                            )}
                          </>
                        );
                      })()
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {t('common.notSpecified')}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* D3FEND Technique */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <ShieldIcon sx={{ color: 'secondary.main' }} />
                      <Typography variant="subtitle2" color="text.secondary">
                        {t('mitigation.fields.d3fendTechnique')}
                      </Typography>
                    </Box>
                    {mitigation.d3fendId ? (
                      <>
                        <Typography variant="h6" fontWeight={600}>
                          {mitigation.d3fendId}
                        </Typography>
                        {mitigation.d3fendName && (
                          <Typography variant="body2" color="text.secondary">
                            {mitigation.d3fendName}
                          </Typography>
                        )}
                        <Button
                          size="small"
                          endIcon={<OpenInNewIcon />}
                          href={getD3fendUrl(mitigation.d3fendId)}
                          target="_blank"
                          sx={{ mt: 1 }}
                        >
                          {t('common.viewDetails')}
                        </Button>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {t('common.notSpecified')}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>

          {/* Status Transition History */}
          <Paper sx={{ p: 3 }}>
            <TransitionHistory
              entityType={EntityType.MITIGATION}
              entityId={mitigation.id}
              maxVisible={5}
            />
          </Paper>
        </Grid>

        {/* Right Column - Sidebar */}
        <Grid size={{ xs: 12, md: 4 }}>
          {/* Parent Risk Card */}
          {risk && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                {t('mitigation.parentRisk')}
              </Typography>
              <Card
                variant="outlined"
                sx={{
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
                onClick={() => navigate(`/risks/${risk.id}`)}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BugIcon sx={{ color: 'warning.main' }} />
                    <Typography variant="subtitle1" fontWeight={600}>
                      {risk.title}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Paper>
          )}

          {/* Resource Planning */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('mitigation.resourcePlanning')}
            </Typography>

            {/* Estimated Cost */}
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}
              >
                <MoneyIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                <Typography variant="subtitle2" color="text.secondary">
                  {t('mitigation.fields.estimatedCost')}
                </Typography>
              </Box>
              <Typography variant="h6" fontWeight={600}>
                {mitigation.estimatedCost
                  ? `${mitigation.estimatedCost.toLocaleString()} NOK`
                  : t('common.notSpecified')}
              </Typography>
            </Box>

            {/* Estimated Effort */}
            <Box>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}
              >
                <ScheduleIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                <Typography variant="subtitle2" color="text.secondary">
                  {t('mitigation.fields.estimatedEffort')}
                </Typography>
              </Box>
              <Typography variant="h6" fontWeight={600}>
                {mitigation.estimatedEffort
                  ? `${mitigation.estimatedEffort} ${t('mitigation.units.days')}`
                  : t('common.notSpecified')}
              </Typography>
            </Box>
          </Paper>

          {/* Timestamps */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('common.timestamps')}
            </Typography>
            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                display="block"
                sx={{ mb: 1 }}
              >
                {t('common.createdAt')}:{' '}
                {new Date(mitigation.createdAt).toLocaleString()}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                display="block"
              >
                {t('common.updatedAt')}:{' '}
                {new Date(mitigation.updatedAt).toLocaleString()}
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Mitigation Edit Form */}
      {mitigation && (
        <MitigationForm
          open={formOpen}
          onClose={() => setFormOpen(false)}
          onSubmit={handleFormSubmit}
          riskId={mitigation.riskId}
          mode="edit"
          initialData={mitigation}
        />
      )}

      {/* Version History Dialog */}
      <VersionHistoryDialog
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
        entityType={EntityType.MITIGATION}
        entityId={mitigation.id}
        entityName={mitigation.title}
      />
    </Box>
  );
};

export default MitigationDetail;
